'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { FileSpreadsheetIcon, File01Icon } from '@terra/ui-icons';
import { Button } from '../../../../../components/styled/button';
import type {
  CoPilotSessionExportArtifact,
  CoPilotSessionExportFormat,
} from '@terra/shared-dto-chat-types';
import { useChatSessionExportDownload } from '../../../session-export/ChatSessionExportProvider';
import { readCoPilotSessionExportArtifact } from '../../../utils/copilot-session-export-artifact';
import type { CardRenderContext } from '../../tool-config-types';
import { PendingObjectReviewCard } from '../../HITL/components/PendingObjectReviewCard';
import { ToolInlineStatusLine } from '../shared/ToolInlineStatusLine';

type CoPilotSessionExportFileCardProps = {
  format: CoPilotSessionExportFormat;
  title: string;
  applicationId: string;
  threadId: string;
  sessionStatus?: 'running' | 'completed' | 'error' | 'aborted';
  requestFilename?: string;
  attachmentFilename?: string;
  csvObjectKinds?: CoPilotSessionExportArtifact['csvObjectKinds'];
  csvSources?: CoPilotSessionExportArtifact['csvSources'];
  csvKind?: CoPilotSessionExportArtifact['csvKind'];
  markdownKind?: CoPilotSessionExportArtifact['markdownKind'];
};

function resolveFileIcon(format: CoPilotSessionExportFormat) {
  if (format === 'csv' || format === 'har') {
    return <FileSpreadsheetIcon size={18} />;
  }

  return <File01Icon size={18} />;
}

function CoPilotSessionExportFileCard({
  format,
  title,
  applicationId,
  threadId,
  sessionStatus,
  requestFilename,
  attachmentFilename,
  csvObjectKinds,
  csvSources,
  csvKind,
  markdownKind,
}: CoPilotSessionExportFileCardProps) {
  const tCommon = useTranslations('designSystem.common');
  const tSessionExport = useTranslations('designSystem.chat.sessionExport');
  const downloadSessionExport = useChatSessionExportDownload();
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = () => {
    if (!downloadSessionExport) {
      console.warn('Co-pilot session export download is not configured');

      return;
    }

    setIsDownloading(true);
    void downloadSessionExport({
      format,
      threadId,
      applicationId,
      sessionStatus,
      requestFilename,
      attachmentFilename,
      csvObjectKinds,
      csvSources,
      csvKind,
      markdownKind,
    }).finally(() => {
      setIsDownloading(false);
    });
  };

  return (
    <PendingObjectReviewCard
      title={title}
      fallbackTitle={tSessionExport('fallbackTitle')}
      statusMessage={tSessionExport('exportReadyStatus')}
      portalVisibilityMessage={tSessionExport('exportReadyHint')}
      icon={resolveFileIcon(format)}
      iconColor="fg.vulnerability.vulnerable"
      iconBorderColor="border.vulnerability.critical"
      iconBg="bg.vulnerability.vulnerable"
      trailingAction={
        <Button
          variant="ghost"
          size="xs"
          onClick={(event) => {
            event.stopPropagation();
            handleDownload();
          }}
          disabled={isDownloading || !downloadSessionExport}
          flexShrink={0}
        >
          {tCommon('download')}
        </Button>
      }
    />
  );
}

type CoPilotSessionExportCardProps = {
  ctx: CardRenderContext;
};

function CoPilotSessionExportCard({ ctx }: CoPilotSessionExportCardProps) {
  const tSessionExport = useTranslations('designSystem.chat.sessionExport');
  const artifact = readCoPilotSessionExportArtifact(
    ctx.toolCallData.output,
    ctx.toolCallData.input,
  );
  const status = ctx.toolCallData.status;
  const isRunning = status === 'started' || status === 'in-progress';
  const isError = status === 'error' || Boolean(ctx.toolCallData.error);

  if (isRunning) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.runningText ?? ctx.labelPresent}
      />
    );
  }

  if (isError) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={ctx.toolCallData.error ?? ctx.labelPast}
        isError
      />
    );
  }

  if (status !== 'completed') {
    return null;
  }

  if (!artifact) {
    return (
      <ToolInlineStatusLine
        icon={ctx.toolIcon}
        text={tSessionExport('exportUnavailable')}
        isError
      />
    );
  }

  return (
    <CoPilotSessionExportFileCard
      format={artifact.format}
      title={artifact.title}
      applicationId={artifact.applicationId}
      threadId={artifact.threadId}
      sessionStatus={artifact.sessionStatus}
      requestFilename={artifact.requestFilename}
      attachmentFilename={artifact.filename}
      csvObjectKinds={artifact.csvObjectKinds}
      csvSources={artifact.csvSources}
      csvKind={artifact.csvKind}
      markdownKind={artifact.markdownKind}
    />
  );
}

export function renderCoPilotSessionExportCard(
  ctx: CardRenderContext,
): React.ReactNode {
  return <CoPilotSessionExportCard ctx={ctx} />;
}
